/**
 * The log — her spreadsheet, with the columns in the order she has them.
 *
 * Edits save as she makes them; there is no Save button, because there was never one in
 * Excel either. Export produces the same file the CLI does, so anyone still expecting the
 * workbook gets the workbook.
 */

import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../api";
import { ActionsPanel } from "../components/ActionsPanel";
import { ClientPicker } from "../components/ClientPicker";
import { HandbookRef, HandbookSearchBox } from "../components/HandbookLookup";
import { formatDate, monthLabel, monthOptions } from "../config";
import type { Client, Item, ItemPatch } from "../types";
import { LOG_STATUSES, NORWOOD_ACTIONS, clientLabel, liveClients } from "../types";
import { ItemLink } from "../components/ItemLink";
import { LogCards, LogItemSheet } from "../components/LogCards";
import { useIsPhone } from "../viewport";

interface Props {
  clients: Client[];
  onError: (error: unknown) => void;
}

/**
 * Her long-form columns. Two of them run past 1,400 characters in her own workbook, so they
 * are edited in a proper textarea rather than a one-line box — and the length cap is the
 * API's own, so the editor refuses what the server would refuse.
 */
const LONG_TEXT = {
  editable: true,
  cellEditor: "agLargeTextCellEditor",
  cellEditorPopup: true,
  cellEditorParams: { maxLength: 5000, rows: 14, cols: 70 },
} as const;

/**
 * How a wrapped cell lays out. Applied inline so that it is part of what AG Grid measures
 * when sizing the row — `pre-wrap` because the newlines in her *Key points* are hers, and
 * top alignment because a fifteen-line cell centred against a one-line date reads badly.
 */
const WRAPPED_CELL = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  // Deliberately no vertical padding. AG Grid sizes a row from the measured height of the
  // cell's content, and padding added here sits outside that measurement — every row then
  // comes up about one line short, which is precisely the clipping this is meant to fix.
  // Breathing room comes from the line height instead.
  lineHeight: "1.5",
  display: "flex",
  alignItems: "flex-start",
} as const;

/** Her Client applicability column is one cell; the model is a list. */
const listToText = (values: string[] | undefined) => (values ?? []).join(", ");
const textToList = (text: string) =>
  text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

export function LogView({ clients, onError }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [client, setClient] = useState("");
  const [status, setStatus] = useState("tracked");
  const [saving, setSaving] = useState<string | null>(null);
  /** What the last edit did beyond saving itself, said once and then cleared. */
  const [note, setNote] = useState("");
  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Full text by default; one line per row when she wants to scan rather than read. */
  const [wrap, setWrap] = useState(true);
  /**
   * The item whose next steps she is editing, or null.
   *
   * Not an AG Grid cell editor: a cell editor hands one value back and the grid writes it in
   * one PATCH, and an item's actions are several records that are added, reworded and deleted
   * independently. The cell shows their summary; `ActionsPanel` writes them.
   */
  const [editingActions, setEditingActions] = useState<Item | null>(null);
  const gridRef = useRef<AgGridReact<Item>>(null);
  /** The search box. One value for both layouts: the grid's quick filter, or the cards'. */
  const [query, setQuery] = useState("");
  /** On a phone, the entry open in the edit sheet. */
  const [editing, setEditing] = useState<Item | null>(null);
  const phone = useIsPhone();

  const load = useCallback(() => {
    setLoading(true);
    api
      .listAllItems({
        status: month ? undefined : status,
        month: month || undefined,
        client: client || undefined,
      })
      .then((found) => {
        // A month query returns every status; the log is what she accepted.
        setItems(month ? found.filter((item) => item.status === status) : found);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [month, client, status, onError]);

  useEffect(load, [load]);

  /** Say what an edit did beyond saving, for a few seconds. */
  const say = useCallback((message: string) => {
    clearTimeout(noteTimer.current);
    setNote(message);
    noteTimer.current = setTimeout(() => setNote(""), 6000);
  }, []);

  useEffect(() => () => clearTimeout(noteTimer.current), []);

  const onCellValueChanged = useCallback(
    async (event: CellValueChangedEvent<Item>) => {
      const field = event.colDef.field as keyof Item | undefined;
      if (!field || !event.data) return;

      const patch: ItemPatch = {};
      if (field === "clients") patch.clients = textToList(String(event.newValue ?? ""));
      else if (field === "regulators") patch.regulators = textToList(String(event.newValue ?? ""));
      else if (field === "tags") patch.tags = textToList(String(event.newValue ?? ""));
      else (patch as Record<string, unknown>)[field] = event.newValue ?? "";

      // Next steps is no longer one of these. An item can hold several actions since
      // 20 September 2026 — one per client, or none — so the cell shows a summary of them and
      // `ActionsPanel` is where they are written. The API refuses the three derived fields on a
      // patch, so a cell editor left here would fail on a click rather than at build time.

      setSaving(event.data.id);
      try {
        const updated = await api.patchItem(event.data.id, patch);
        setItems((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      } catch (error) {
        onError(error);
        load(); // put the grid back to what the server actually holds
      } finally {
        setSaving(null);
      }
    },
    [onError, load, say],
  );

  /** The clients a tag can name. Drives the picker and how a selection of everyone reads. */
  const liveNames = useMemo(() => liveClients(clients), [clients]);

  /**
   * The cards' version of the grid's quick filter: every word must appear somewhere in the
   * entry's text. Newest first, which is the grid's default sort.
   */
  const cards = useMemo(() => {
    if (!phone) return [];
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const matching = words.length
      ? items.filter((item) => {
          const text = [
            item.title,
            item.reference,
            item.summary,
            item.key_points,
            item.regulator_raw,
            item.regulator,
            item.clients_raw,
            ...(item.clients ?? []),
            ...(item.tags ?? []),
            item.next_steps,
          ]
            .join(" ")
            .toLowerCase();
          return words.every((word) => text.includes(word));
        })
      : items;
    return [...matching].sort((a, b) => (b.published_at ?? "").localeCompare(a.published_at ?? ""));
  }, [phone, items, query]);

  /** The sheet's save: one PATCH with everything she changed, then the row from the server. */
  const saveSheet = async (item: Item, patch: ItemPatch) => {
    if (!Object.keys(patch).length) {
      setEditing(null);
      return;
    }
    try {
      const updated = await api.patchItem(item.id, patch);
      setItems((current) =>
        // A status change takes it out of the list she is looking at, as the filter says.
        updated.status === status
          ? current.map((row) => (row.id === updated.id ? updated : row))
          : current.filter((row) => row.id !== updated.id),
      );
      setEditing(null);
      say("Saved.");
    } catch (error) {
      onError(error);
    }
  };

  const columns = useMemo<ColDef<Item>[]>(
    () => [
      {
        headerName: "Date",
        field: "published_at",
        width: 110,
        valueFormatter: (params) => formatDate(params.value),
        sort: "desc",
        editable: false,
      },
      {
        headerName: "Regulator",
        field: "regulators",
        width: 120,
        valueGetter: (params) =>
          params.data?.regulator_raw || listToText(params.data?.regulators) || params.data?.regulator,
        editable: true,
      },
      {
        headerName: "Reference",
        field: "reference",
        width: 130,
        editable: true,
        // A Handbook reference she has already typed becomes a link to the rule, opening in a
        // window beside the log rather than a tab that hides it. Only if it
        // resolves against the real tree: a reference that does not stays plain text, because
        // the FCA's site returns 200 for any path and a made-up link would look fine and fail.
        cellRenderer: (params: { value?: string }) =>
          params.value ? <HandbookRef reference={params.value} /> : null,
      },
      {
        headerName: "Title",
        field: "title",
        width: 340,
        editable: true,
        cellRenderer: (params: { data?: Item; value?: string }) => (
          <ItemLink url={params.data?.url}>{params.value}</ItemLink>
        ),
      },
      {
        // Her workbook has a URL column and so does this: the Title cell links, but a link is
        // not the address. She copies it into emails and papers, and a cell she can select and
        // copy is the whole point — so the text is the URL, not the word "link".
        headerName: "URL",
        field: "url",
        width: 220,
        // Read-only, and not an oversight: the item's id is sha256 of its canonical URL, so an
        // edited URL would no longer match the id derived from it. `url` is deliberately absent
        // from the API's patchable fields for the same reason — this column agrees with it
        // rather than offering an edit that would 400.
        editable: false,
        cellRenderer: (params: { value?: string }) =>
          params.value ? (
            <ItemLink url={params.value} title={params.value}>
              {params.value}
            </ItemLink>
          ) : null,
      },
      { headerName: "Summary", field: "summary", width: 320, ...LONG_TEXT },
      // Read-only, and beside her own Summary rather than replacing it: she can see what
      // was drafted for an item she has already tracked, and copy from it if she wants it.
      {
        headerName: "AI summary",
        field: "ai_summary",
        width: 300,
        ...LONG_TEXT,
        editable: false,
        headerTooltip: "Drafted, not applied. Accept it in the review queue to put it in Summary.",
        cellClass: "ai-cell",
      },
      { headerName: "Key points", field: "key_points", width: 280, ...LONG_TEXT },
      { headerName: "Scope", field: "applies_to", width: 180, ...LONG_TEXT },
      {
        // Read-only, and opening it opens the panel. The cell holds the *derived* summary of
        // this item's actions — "Faciit: map to CASS 5" on one line, "Aveni: brief the product
        // team" on the next — so typing over it would mean typing over a join. Her workbook's
        // one Next Steps column is still what the Excel export writes, from this same value.
        headerName: "Next steps",
        field: "next_steps",
        width: 240,
        editable: false,
        wrapText: true,
        autoHeight: true,
        cellStyle: WRAPPED_CELL,
        cellRenderer: (params: { data?: Item; value?: string }) => (
          <button
            type="button"
            className="cell-actions"
            title="Add, reword or complete this item's next steps"
            onClick={() => params.data && setEditingActions(params.data)}
          >
            {params.value ? <span>{params.value}</span> : <span className="muted">Add…</span>}
          </button>
        ),
      },
      {
        headerName: "Norwood",
        field: "norwood_action",
        width: 140,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: [...NORWOOD_ACTIONS] },
      },
      {
        // Ticked, not typed. Free text is how the log came to say "All" on well over half the
        // rows she had tagged, and a name she has to spell is a name a filter can miss.
        headerName: "Clients",
        field: "clients",
        width: 170,
        editable: true,
        cellEditor: ClientPicker,
        cellEditorPopup: true,
        cellEditorParams: { live: liveNames },
        // Her word for a selection of everyone, derived rather than stored — see
        // `clientLabel`. `clients_raw` is the fallback only when there is no structured value
        // to render, which is what keeps her "check" and "Cyber" notes visible.
        valueGetter: (params) =>
          (params.data?.clients ?? []).length
            ? clientLabel(params.data?.clients ?? [], liveNames)
            : params.data?.clients_raw || "",
      },
      {
        // Hers, not `ai_tags`. Editable as comma-separated text like the other list
        // columns, so the grid keeps one idiom rather than growing a second editor.
        headerName: "Tags",
        field: "tags",
        width: 200,
        editable: true,
        valueGetter: (params) => listToText(params.data?.tags),
      },
      {
        // Derived from the item's actions — "complete" only when every one of them is. Marking
        // work done belongs to the action that is done, which is why this is no longer a
        // dropdown: with three actions on a row, one status could only ever be a summary, and
        // setting a summary says nothing about which piece of work she finished.
        headerName: "Done",
        field: "next_steps_status",
        width: 100,
        editable: false,
        headerTooltip: "From this item's next steps. Mark one done in the Next steps cell.",
      },
      {
        headerName: "Board",
        field: "board_flag",
        width: 90,
        editable: true,
        cellDataType: "boolean",
      },
      { headerName: "Category", field: "category", width: 150, editable: true },
      {
        headerName: "Status",
        field: "status",
        width: 110,
        editable: true,
        cellEditor: "agSelectCellEditor",
        // Archived is how an item she stopped tracking comes back: set it to tracked here.
        cellEditorParams: { values: LOG_STATUSES },
      },
    ],
    [liveNames],
  );

  const exportToExcel = async () => {
    try {
      const result = await api.createExport({
        status,
        month: month || undefined,
        client: client || undefined,
      });
      // A pre-signed link, valid for fifteen minutes — let the browser fetch it.
      window.location.assign(result.url);
    } catch (error) {
      onError(error);
    }
  };

  return (
    <div className="log">
      <div className="toolbar handbook-bar">
        <HandbookSearchBox placeholder="Look up a rule — “creditworthiness”, “CONC 5”…" />
      </div>

      <div className="toolbar">
        <select value={month} onChange={(event) => setMonth(event.target.value)}>
          <option value="">All months</option>
          {monthOptions().map((key) => (
            <option key={key} value={key}>
              {monthLabel(key)}
            </option>
          ))}
        </select>
        <select value={client} onChange={(event) => setClient(event.target.value)}>
          <option value="">All clients</option>
          {clients.map((entry) => (
            <option key={entry.id} value={entry.name}>
              {entry.name}
            </option>
          ))}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="tracked">Tracked</option>
          <option value="new">Awaiting review</option>
          <option value="dismissed">Dismissed</option>
          <option value="reviewed">Reviewed</option>
          <option value="archived">Archived (stopped tracking)</option>
        </select>
        <input
          type="search"
          className="log-search"
          placeholder={phone ? "Search the log…" : "Search the table…"}
          aria-label="Search the log"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <span className="spacer" />
        <label className="inline desk-only">
          <input type="checkbox" checked={wrap} onChange={(event) => setWrap(event.target.checked)} />
          Full text
        </label>
        <span className="muted">
          {loading
            ? "Loading…"
            : phone && query
              ? `${cards.length} of ${items.length}`
              : `${items.length} item${items.length === 1 ? "" : "s"}`}
          {saving && " · saving…"}
        </span>
        {note && <span className="edit-note">{note}</span>}
        <button className="desk-only" onClick={exportToExcel} disabled={loading || !items.length}>
          Export to Excel
        </button>
      </div>

      {phone ? (
        loading ? null : (
          <LogCards
            items={cards}
            live={liveNames}
            onEdit={setEditing}
            onActions={setEditingActions}
          />
        )
      ) : (
      <div className="ag-theme-quartz grid">
        <AgGridReact<Item>
          // Remounting is the reliable way to switch autoHeight on and off; at a few
          // hundred rows it is instant, and it beats coaxing the grid into remeasuring.
          key={wrap ? "wrapped" : "compact"}
          ref={gridRef}
          rowData={items}
          columnDefs={columns}
          getRowId={(params) => params.data.id}
          quickFilterText={query}
          onCellValueChanged={onCellValueChanged}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
            editable: false,
            // Her summaries and key points run to 1,400 characters. Truncating them in the
            // grid hides the part that says what a firm actually has to do.
            wrapText: wrap,
            autoHeight: wrap,
            // Inline, not in the stylesheet: this is the layout AG Grid measures when it
            // decides how tall the row should be.
            cellStyle: wrap ? WRAPPED_CELL : undefined,
          }}
          // Fonts and layout settle after the first measurement, which otherwise leaves the
          // tallest rows a line or two short.
          onFirstDataRendered={(event) => event.api.resetRowHeights()}
          stopEditingWhenCellsLoseFocus
          animateRows={false}
          // A compliance log is read as much as edited; single click to edit would fight that.
          singleClickEdit={false}
          tooltipShowDelay={300}
        />
      </div>
      )}

      {editing && (
        <LogItemSheet
          item={editing}
          clients={clients}
          live={liveNames}
          onClose={() => setEditing(null)}
          onSave={(patch) => saveSheet(editing, patch)}
        />
      )}

      {editingActions && (
        <ActionsPanel
          itemId={editingActions.id}
          itemTitle={editingActions.title}
          live={liveNames}
          alsoNamed={editingActions.clients}
          onClose={() => setEditingActions(null)}
          // Reload rather than patch the row in place: the three cells this changed are derived
          // server-side from every action on the item, so what they now say is the server's
          // answer and not one this screen can work out for itself.
          onChanged={load}
          onError={onError}
        />
      )}
    </div>
  );
}
